import { useQueries, useQuery } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../lib/config';
import { useShigoSession } from '../../lib/useShigoSession';
import { createDashboardClient } from './api';
import { DashboardScope, type DashboardDateRange } from './model';
import {
  supersedableDashboardQueryFn,
  useAbortSupersededDashboardQueries,
} from './supersededDashboardRequests';

export const dashboardQueryKey = ['dashboard', 'main'] as const;

export function dashboardRangeKey(range: DashboardDateRange) {
  return range.preset ? `preset:${range.preset}` : `custom:${range.dateFrom}:${range.dateTo}`;
}

export function deduplicateDashboardRanges(ranges: DashboardDateRange[]) {
  return [...new Map(ranges.map((range) => [dashboardRangeKey(range), range])).values()];
}

function useDashboardClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  return {
    client: createDashboardClient({
      config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session?.accessToken ?? '',
    }),
    enabled: Boolean(session?.accessToken),
  };
}

export function useDashboardOverviewQueries(
  ranges: DashboardDateRange[],
  scope: DashboardScope = DashboardScope.Main,
) {
  const { client, enabled } = useDashboardClient();
  const uniqueRanges = deduplicateDashboardRanges(ranges);
  const queryKeys = uniqueRanges.map(
    (range) => [...dashboardQueryKey, scope, 'overview', dashboardRangeKey(range)] as const,
  );
  const queries = useQueries({
    queries: uniqueRanges.map((range, index) => ({
      queryKey: queryKeys[index] ?? [],
      queryFn: supersedableDashboardQueryFn(queryKeys[index] ?? [], (signal) =>
        client.overview(range, signal, scope),
      ),
      enabled,
    })),
  });
  useAbortSupersededDashboardQueries(queryKeys);
  return new Map(uniqueRanges.map((range, index) => [dashboardRangeKey(range), queries[index]]));
}

export function useDashboardLastImportQuery() {
  const { client, enabled } = useDashboardClient();
  return useQuery({
    queryKey: [...dashboardQueryKey, 'last-import'],
    queryFn: () => client.lastImport(),
    enabled,
    refetchInterval: 300_000,
  });
}
